/**
 * The composer's magnifier: pick the CaseBlink library sources Casey should
 * research for the next message (case law, the INA and C.F.R., the manuals,
 * ...). The picked ids ride on the message as `metadata.research_sources`, and
 * the server tells the agent to search exactly those sources with the
 * library's tools. With none picked the trigger is a small icon; with some it
 * is a removable chip that says which.
 */
import { ArrowRight, Check, Search, X } from "lucide-react";
import { useState, type FC, type MouseEvent } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  RESEARCH_PATH,
  RESEARCH_SOURCES,
  sourceById,
  type ResearchSourceId
} from "@/features/research";
import { cn } from "@/lib/utils";

const CHIP =
  "h-6 gap-1 rounded-full border-transparent bg-info-soft px-2 pr-1 text-sm font-medium text-info shadow-none hover:bg-info-soft hover:text-info aria-expanded:bg-info-soft";

/** "Case Law", "Case Law +2", or "All Sources". */
export const sourcesLabel = (ids: readonly ResearchSourceId[]): string => {
  if (!ids.length) return "";
  if (ids.length === RESEARCH_SOURCES.length) return "All Sources";
  const first = sourceById(ids[0]).label;
  return ids.length === 1 ? first : `${first} +${ids.length - 1}`;
};

type Props = {
  value: ResearchSourceId[];
  onChange: (ids: ResearchSourceId[]) => void;
  agentLabel?: string;
  disabled?: boolean;
};

export const ResearchSourcesPicker: FC<Props> = ({
  value,
  onChange,
  agentLabel = "Casey",
  disabled
}) => {
  const [open, setOpen] = useState(false);
  const picked = new Set(value);
  // Kept in the library's order, whatever order they were ticked in.
  const toggle = (id: ResearchSourceId) =>
    onChange(
      RESEARCH_SOURCES.map((s) => s.id).filter((s) =>
        s === id ? !picked.has(s) : picked.has(s)
      )
    );
  const clear = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange([]);
  };
  const names = value.map((id) => sourceById(id).label).join(", ");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            {value.length ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                className={CHIP}
                aria-label={`Researching ${names}`}
                data-testid="research-sources-chip"
              >
                <Search className="size-3" />
                <span className="max-w-40 truncate">{sourcesLabel(value)}</span>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Clear Research Sources"
                  data-testid="research-sources-clear"
                  onClick={clear}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onChange([]);
                    }
                  }}
                  className="ml-0.5 inline-flex size-4 items-center justify-center rounded-full hover:bg-fill-2"
                >
                  <X className="size-3" />
                </span>
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                disabled={disabled}
                className="size-6 rounded-full text-fg-3 hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground"
                aria-label="Research the Library"
                data-testid="research-sources-trigger"
              >
                <Search className="size-3.5" />
              </Button>
            )}
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          {value.length
            ? `${agentLabel} researches ${names} for your next message`
            : "Research the immigration library"}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-80 p-0"
        data-testid="research-sources-picker"
      >
        <div className="flex flex-col gap-0.5 px-3 pt-3 pb-2">
          <span className="text-base font-medium text-fg">Research In</span>
          <p className="text-sm text-fg-3">
            {agentLabel} searches these sources in the CaseBlink immigration
            library for your next message, and cites what it finds.
          </p>
        </div>
        <div
          className="flex flex-col px-1.5 pb-1.5"
          role="group"
          aria-label="Research Sources"
        >
          {RESEARCH_SOURCES.map((s) => {
            const checked = picked.has(s.id);
            return (
              <button
                key={s.id}
                type="button"
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggle(s.id)}
                className={cn(
                  "flex items-start gap-2 rounded-md px-1.5 py-1.5 text-left hover:bg-fill-1",
                  checked && "bg-fill-1"
                )}
                data-testid={`research-source-option-${s.id}`}
              >
                <span
                  className={cn(
                    "mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-xs ring-1 ring-line-3",
                    checked && "bg-primary text-primary-foreground ring-primary"
                  )}
                >
                  {checked ? <Check className="size-3" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-base font-medium text-fg">
                    {s.label}
                  </span>
                  <span className="line-clamp-2 text-sm text-fg-3">
                    {s.blurb}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1 border-t border-line-2 px-2 py-1.5">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() =>
              onChange(
                value.length === RESEARCH_SOURCES.length
                  ? []
                  : RESEARCH_SOURCES.map((s) => s.id)
              )
            }
            data-testid="research-sources-all"
          >
            {value.length === RESEARCH_SOURCES.length
              ? "Clear All"
              : "Select All"}
          </Button>
          <Button
            asChild
            size="sm"
            variant="ghost"
            className="ml-auto text-fg-3"
          >
            <Link to={RESEARCH_PATH} onClick={() => setOpen(false)}>
              Open Research Library
              <ArrowRight />
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
